import { useState, useEffect } from 'react';
import { Star, ThumbsUp, Flag, ChevronDown, User, Award } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${window.location.origin}/api`;

// Star Rating Input Component
const StarRatingInput = ({ value, onChange }) => {
  const [hoverValue, setHoverValue] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          className="p-1 transition-transform hover:scale-110"
          data-testid={`star-input-${star}`}
        >
          <Star
            className={`w-8 h-8 transition-colors ${
              star <= (hoverValue || value)
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-zinc-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Star Rating Display Component
const StarRating = ({ rating, size = 'sm' }) => {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'
          }`}
        />
      ))}
    </div>
  );
};

// Reputation Badge Component
const ReputationBadge = ({ score, totalReviews }) => {
  let level = 'New';
  let color = 'text-zinc-400 bg-zinc-800';
  
  if (score >= 5) {
    level = 'Expert';
    color = 'text-yellow-400 bg-yellow-500/20';
  } else if (score >= 3) {
    level = 'Trusted';
    color = 'text-green-400 bg-green-500/20';
  } else if (score >= 1) {
    level = 'Active';
    color = 'text-blue-400 bg-blue-500/20';
  }

  return (
    <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${color}`}>
      <Award className="w-3 h-3" />
      <span>{level}</span>
      <span className="opacity-60">• {totalReviews} reviews</span>
    </div>
  );
};

// Single Review Component
const ReviewCard = ({ review, onHelpful, onFlag, currentUserId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLong = review.content.length > 200;

  const handleHelpful = async () => {
    try {
      const response = await fetch(`${API}/reviews/${review.review_id}/helpful`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ helpful: true })
      });

      if (response.ok) {
        onHelpful(review.review_id);
      } else if (response.status === 401) {
        toast.error('Please sign in to vote');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit vote');
      }
    } catch (error) {
      toast.error('Failed to submit vote');
    }
  };

  const handleFlag = async () => {
    try {
      const response = await fetch(`${API}/reviews/${review.review_id}/flag`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Review flagged for moderation');
        onFlag(review.review_id);
      } else if (response.status === 401) {
        toast.error('Please sign in to flag reviews');
      }
    } catch (error) {
      toast.error('Failed to flag review');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-zinc-900/50 border border-zinc-800"
      data-testid={`review-${review.review_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          {review.reviewer?.picture ? (
            <img
              src={review.reviewer.picture}
              alt={review.reviewer.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-500" />
            </div>
          )}
          <div>
            <p className="font-medium text-white">{review.reviewer?.name || 'Anonymous'}</p>
            <ReputationBadge 
              score={review.reviewer?.reputation_score || 0} 
              totalReviews={review.reviewer?.total_reviews || 0}
            />
          </div>
        </div>
        <div className="text-right">
          <StarRating rating={review.rating} />
          <p className="text-zinc-500 text-xs mt-1">
            {new Date(review.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Title */}
      {review.title && (
        <h4 className="font-semibold text-white mb-2">{review.title}</h4>
      )}

      {/* Content */}
      <p className={`text-zinc-300 text-sm leading-relaxed ${!isExpanded && isLong ? 'line-clamp-3' : ''}`}>
        {review.content}
      </p>
      
      {isLong && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-zinc-400 text-sm mt-2 hover:text-white flex items-center gap-1"
        >
          {isExpanded ? 'Show less' : 'Read more'}
          <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      )}

      {/* Footer */}
      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800">
        <button
          onClick={handleHelpful}
          disabled={review.user_id === currentUserId}
          className={`flex items-center gap-2 text-sm transition-colors ${
            review.user_id === currentUserId
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-400 hover:text-white'
          }`}
          data-testid={`helpful-${review.review_id}`}
        >
          <ThumbsUp className="w-4 h-4" />
          Helpful ({review.helpful_count || 0})
        </button>
        
        {review.user_id !== currentUserId && (
          <button
            onClick={handleFlag}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-red-400 transition-colors"
            data-testid={`flag-${review.review_id}`}
          >
            <Flag className="w-4 h-4" />
            Flag
          </button>
        )}
      </div>
    </motion.div>
  );
};

// Main Reviews Component
export default function Reviews({ businessId, businessName }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [sortBy, setSortBy] = useState('recent');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    content: ''
  });

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const user = await response.json();
          setCurrentUserId(user.user_id);
        }
      } catch (error) {
        console.log('Not logged in');
      }
    };
    fetchUser();
  }, []);

  // Fetch reviews
  const fetchReviews = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        sort: sortBy
      });
      
      const response = await fetch(`${API}/businesses/${businessId}/reviews?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews);
        setPagination({ page: data.page, pages: data.pages, total: data.total });
        
        // Check if current user has already reviewed
        if (currentUserId) {
          const hasReviewed = data.reviews.some(r => r.user_id === currentUserId);
          setUserHasReviewed(hasReviewed);
        }
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [businessId, sortBy, currentUserId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();

    if (formData.rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!formData.content.trim()) {
      toast.error('Please write a review');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: businessId,
          ...formData
        })
      });

      if (response.ok) {
        toast.success('Review submitted successfully!');
        setShowForm(false);
        setFormData({ rating: 0, title: '', content: '' });
        setUserHasReviewed(true);
        fetchReviews();
      } else if (response.status === 401) {
        toast.error('Please sign in to write a review');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to submit review');
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = (reviewId) => {
    setReviews(reviews.map(r => 
      r.review_id === reviewId 
        ? { ...r, helpful_count: (r.helpful_count || 0) + 1 }
        : r
    ));
  };

  const handleFlag = (reviewId) => {
    setReviews(reviews.filter(r => r.review_id !== reviewId));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Reviews
          </h2>
          <p className="text-zinc-400">
            {pagination.total} {pagination.total === 1 ? 'review' : 'reviews'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800 text-white" data-testid="reviews-sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="recent" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                Most Recent
              </SelectItem>
              <SelectItem value="helpful" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                Most Helpful
              </SelectItem>
              <SelectItem value="rating_high" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                Highest Rated
              </SelectItem>
              <SelectItem value="rating_low" className="text-zinc-300 focus:bg-zinc-800 focus:text-white">
                Lowest Rated
              </SelectItem>
            </SelectContent>
          </Select>

          {/* Write Review Button */}
          {!userHasReviewed && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="btn-primary text-sm py-2 px-4"
              data-testid="write-review-button"
            >
              Write a Review
            </button>
          )}
        </div>
      </div>

      {/* Review Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <form onSubmit={handleSubmitReview} className="p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 space-y-4">
              <h3 className="text-lg font-semibold text-white mb-4">
                Review {businessName}
              </h3>

              {/* Rating */}
              <div className="space-y-2">
                <Label className="text-zinc-300">Your Rating</Label>
                <StarRatingInput
                  value={formData.rating}
                  onChange={(rating) => setFormData(prev => ({ ...prev, rating }))}
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="review-title" className="text-zinc-300">Title (optional)</Label>
                <Input
                  id="review-title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Summarize your experience"
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600"
                  data-testid="review-title-input"
                />
              </div>

              {/* Content */}
              <div className="space-y-2">
                <Label htmlFor="review-content" className="text-zinc-300">Your Review</Label>
                <Textarea
                  id="review-content"
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Share your experience with this business..."
                  rows={4}
                  className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 resize-none"
                  data-testid="review-content-input"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary text-sm py-2 px-4"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary text-sm py-2 px-4 disabled:opacity-50"
                  data-testid="submit-review-button"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reviews List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-xl" />
          ))}
        </div>
      ) : reviews.length > 0 ? (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard
              key={review.review_id}
              review={review}
              onHelpful={handleHelpful}
              onFlag={handleFlag}
              currentUserId={currentUserId}
            />
          ))}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-center gap-2 pt-4">
              {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => fetchReviews(page)}
                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                    pagination.page === page
                      ? 'bg-white text-black'
                      : 'bg-zinc-900 text-zinc-400 hover:text-white'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 rounded-xl bg-zinc-900/30 border border-zinc-800">
          <Star className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No reviews yet</h3>
          <p className="text-zinc-500 mb-4">Be the first to review this business!</p>
          {!userHasReviewed && (
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary text-sm py-2 px-4"
            >
              Write a Review
            </button>
          )}
        </div>
      )}
    </div>
  );
}
